/**
 * Words and numbers on the V1 screens, in the one language the toggle picked (Hindi by default;
 * never Hindi and English together): kilos with two decimals ("49.28 किलो"), money with small
 * paise ("₹36,673.50"), dates as "2 अक्टूबर" / "2 October".
 */
import { currentLocale, translate, type MessageKey, type MessageVars } from '@/i18n'
import { formatPaise, type Paise } from '@/lib/money'
import { englishRupees, hindiRupees } from './hindiWords'

/** A message in the language on screen. */
export const tr = (k: MessageKey, v?: MessageVars) => translate(currentLocale(), k, v)

/** Sentences read aloud one after another ("।" in Hindi, "." in English). */
export const sentences = (parts: string[]) =>
  parts.filter(Boolean).join(currentLocale() === 'hi' ? '। ' : '. ')

/** An amount in words, as the farmer hears it read off the slip. */
export const amountWords = (paise: number) =>
  currentLocale() === 'hi' ? hindiRupees(paise) : englishRupees(paise)

const dateLocale = () => (currentLocale() === 'hi' ? 'hi-IN' : 'en-IN')

/** Speech text: the message without its bold marks. */
export const plain = (text: string) => text.replaceAll('**', '')

const KG = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 3 })

/** 49280 → "49.28"; 49285 → "49.285" (the scale shows 10 g steps; sacks can make 5 g). */
export function kg(grams: number): string {
  // Nothing yet reads "0 किलो" on the boards, not "0.00".
  return grams === 0 ? '0' : KG.format(grams / 1000)
}

/** Whole kilos for the day tiles: 3580412 → "3,580". */
export const kgWhole = (grams: number) =>
  new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.round(grams / 1000))

export const money = (paise: Paise) => formatPaise(paise)

/** "₹78" for a whole-rupee price, else "₹78.50". */
export const price = (paise: Paise) => formatPaise(paise, { trimZeroPaise: true })

const IST = 'Asia/Kolkata'
export const fmtDate = (at: string | number | Date) =>
  new Intl.DateTimeFormat(dateLocale(), { day: 'numeric', month: 'long', timeZone: IST }).format(
    new Date(at),
  )
export const fmtTime = (at: string | number | Date) =>
  new Intl.DateTimeFormat('en-IN', {
    hour: 'numeric',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: IST,
  }).format(new Date(at))

/** "आज 7:40" / "Today 7:40", "कल 7:40" / "Yesterday 7:40", else the date. */
export function fmtWhen(at: number, now = Date.now()): string {
  const day = (t: number) => new Date(t).toLocaleDateString('en-CA', { timeZone: IST })
  if (day(at) === day(now)) return tr('gate.v1.when.today', { time: fmtTime(at) })
  if (day(at) === day(now - 86_400_000)) return tr('gate.v1.when.yesterday', { time: fmtTime(at) })
  return fmtDate(at)
}

/** The number on a tag or seal as people read it: the last part of the code ("RJ-BELU-0042" → "0042"). */
export const shortCode = (payload: string) => payload.split(/[-/]/).at(-1) ?? payload

/** Lot code as on the boards' lot cards: the device prefix and counter ("HL-2026-1012-D1-0001" → "D1-0001"). */
export const shortLot = (code: string) => code.split('-').slice(-2).join('-')

/** The lot counter on the row slate ("0001"). */
export const slate = (code: string) => code.split('-').at(-1) ?? code

/** The crop in the language on screen (roster name, else the app's own word, else the other name). */
export function cropName(
  crops: readonly { crop_id: string; name_hi?: string | null; name_en?: string | null }[],
  cropId: string | null | undefined,
): string {
  if (!cropId) return ''
  const c = crops.find((x) => x.crop_id === cropId)
  const hindi = currentLocale() === 'hi'
  const named = hindi ? c?.name_hi : c?.name_en
  if (named) return named
  const key = `gate.crop.${cropId}` as MessageKey
  const word = translate(currentLocale(), key)
  return word !== key ? word : ((hindi ? c?.name_en : c?.name_hi) ?? cropId)
}

/**
 * A crop or product by the server's name, in the language on screen ("Moong 500 g" → "मूँग",
 * "Groundnut kernels" → "मूँगफली"); the name itself when there is no word for its crop.
 */
export function cropWord(name: string | null | undefined): string {
  if (!name) return ''
  const first = name.trim().split(/\s+/)[0]?.toLowerCase() ?? ''
  const key = `gate.crop.${first}` as MessageKey
  const word = translate(currentLocale(), key)
  return word !== key ? word : name
}

const SACK_LETTERS_HI = ['क', 'ख', 'ग', 'घ', 'च', 'छ', 'ज', 'झ', 'ट', 'ठ']

/**
 * A sack type by name: in English its roster name; in Hindi its Hindi name when the server sends
 * one, else "बारदाना क / ख / ग" in roster order (the boards name sacks by letter).
 */
export function sackName(
  types: readonly { id: string; name: string; name_hi?: unknown }[],
  id: string,
): string {
  const i = types.findIndex((s) => s.id === id)
  const s = types[i]
  if (!s) return ''
  if (currentLocale() !== 'hi') return s.name
  if (typeof s.name_hi === 'string' && s.name_hi) return s.name_hi
  return tr('gate.v1.weigh.sackLetter', { letter: SACK_LETTERS_HI[i] ?? String(i + 1) })
}

/** The roster number as printed on his card and typed on the keypad ("BELU-014" → "014"). */
export const rosterShort = (rosterNo: string) => rosterNo.replace(/^.*-/, '')
